import React from 'react';
import PropTypes from 'prop-types';
import RichText from '$partials/global/rich-text';
import Button from '$partials/ui/button';
import { Image } from '$partials/media';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */

const ModuleLargeCard = ({
    cardTitle,
    ctaLabel,
    ctaLink,
    editorialHighlightVariant,
    eyebrowIcon: icon = {},
    eyebrowTitle,
    image = {},
    imageGreyOutline,
    imageLayout = 'Full',
    imagePaddingBottom,
    isCarousel,
    richText,
    parent
}) => {
    if (parent === 'Home') {
        return (
            <section className='h-full relative'>
                <div
                    className={`h-full flex ${editorialHighlightVariant ? 'flex-wrap-reverse gap-y-20' : 'flex-wrap'}`}>
                    <div className='flex flex-col w-full sm:w-6/12 md:w-5/12 px-10 sm:px-5 md:px-10 sm:pr-30 md:ml-0'>
                        <div className='flex items-center mb-20 sm:mb-13 md:mb-20'>
                            {icon && (
                                <div
                                    className='mr-10'
                                    style={{
                                        fontSize: '18px',
                                        width: `${(icon?.width / icon?.height)}em` ,
                                        maxWidth: '100%'
                                    }}>
                                    <Image
                                        {...icon}
                                        loading={''}/>
                                </div>
                            )}
                            {eyebrowTitle && (
                                <p className='type-style-7 text-orange'>
                                    {eyebrowTitle}
                                </p>
                            )}
                        </div>
                        <p className='type-style-3'>{cardTitle}</p>
                        {richText && (
                            <RichText
                                className={`mt-20 sm:mt-[6px] md:mt-10 type-style-5 rich-text ${editorialHighlightVariant ? 'text-light-grey' : ''}`}
                                {...richText}/>
                        )}

                        {ctaLabel && ctaLink && (
                            <Button
                                as='a'
                                className='mt-20 flex items-center self-start button button--primary text-white rounded-button type-style-7 px-20 py-13'
                                href={ctaLink}
                                type='button'>
                                {ctaLabel}
                            </Button>
                        )}
                    </div>

                    {/* Spacer */}
                    <div className='hidden md:flex w-1/12 md:px-10'/>

                    {/* Image */}
                    <div className={'w-11/12 sm:w-6/12 flex flex-col mt-18 sm:mt-0 px-10 sm:px-5 md:px-10'}>
                        <div
                            className={`w-full rounded-button overflow-hidden ${imageGreyOutline ? 'border border-border-grey ' : ''}`}
                            style={isCarousel ? {
                                fontSize: '500px',
                                width: `${(image?.width / image?.height)}em` ,
                                maxWidth: '100%'
                            } : {}}>
                            {
                                image?.contentType?.includes('video/') ? (
                                    <video
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        src={image?.src}
                                        width={'100%'}/>
                                ) : <Image {...image}/>
                            }
                        </div>
                    </div>
                </div>
            </section>
        );
    }
    return (
        <section className='h-full relative px-10 sm:px-5 md:px-10'>
            <div
                className={`bg-sand rounded-button h-full flex flex-wrap justify-between pt-30 md:pt-50 ${isCarousel ? 'pb-70 md:pb-50' : imagePaddingBottom ? 'pb-30 md:pb-50' : ''} mt-0`}>
                <div className={`w-full sm:w-8/12 md:w-4/12 flex flex-col px-30 md:px-0 md:pl-50 ${imagePaddingBottom ? '' : 'md:pb-50'}`}>
                    <div className='flex items-center mb-20'>
                        {icon && (
                            <div
                                className='mr-10'
                                style={{
                                    fontSize: '18px',
                                    width: `${(icon?.width / icon?.height)}em` ,
                                    maxWidth: '100%'
                                }}>
                                <Image
                                    {...icon}
                                    loading={''}/>
                            </div>
                        )}
                        {eyebrowTitle && (
                            <p className='type-style-7 text-orange'>
                                {eyebrowTitle}
                            </p>
                        )}
                    </div>
                    <p className='type-style-3'>{cardTitle}</p>
                    {richText && (
                        <RichText
                            className='mt-[7px] sm:mt-10 md:mt-13 type-style-5 rich-text'
                            {...richText}/>
                    )}

                    {ctaLabel && ctaLink && (
                        <Button
                            as='a'
                            className='mt-15 md:mt-20 flex items-center self-start button button--primary text-white rounded-button type-style-7 px-20 py-13'
                            href={ctaLink}
                            type='button'>
                            {ctaLabel}
                        </Button>
                    )}
                </div>

                {/* Image */}
                <div
                    className={`w-full md:w-8/12 flex flex-col px-30 md:px-0 md:pl-60 md:pr-50 mt-40 md:mt-0 ${isCarousel ? 'items-center md:items-start' : imagePaddingBottom ? '' : 'md:self-end'}`}>
                    <div
                        className={`w-full ${imageGreyOutline ? 'border border-border-grey ' : ''}${imageLayout === 'Full' || isCarousel ? '': 'md:w-1/2'}`}
                        style={isCarousel ? {
                            fontSize: '500px',
                            width: `${(image?.width / image?.height)}em` ,
                            maxWidth: '100%'
                        } : {}}>
                        {
                            image?.contentType?.includes('video/') ? (
                                <video
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    src={image?.src}
                                    width={'100%'}/>
                            ) : <Image {...image}/>
                        }
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

ModuleLargeCard.propTypes = {
    backgroundColor: PropTypes.string,
    cardTitle: PropTypes.string,
    ctaLabel: PropTypes.string,
    ctaLink: PropTypes.string,
    editorialHighlightVariant: PropTypes.bool,
    eyebrowIcon: PropTypes.shape({
        src: PropTypes.string
    }),
    eyebrowTitle: PropTypes.string,
    image: PropTypes.shape({
        src: PropTypes.string.isRequired
    }).isRequired,
    imageGreyOutline: PropTypes.bool,
    imageLayout: PropTypes.oneOf(['Full', 'Half']),
    imagePaddingBottom: PropTypes.bool,
    isCarousel: PropTypes.bool,
    richText: PropTypes.object,
    parent: PropTypes.bool,
};

export default ModuleLargeCard;
